// DateRangeModalPicker.jsx (or rename to BasicDateTimeRangePicker.jsx)
import React, { useState, useEffect, useContext } from "react";
import { Modal, Box, Button, Backdrop } from "@mui/material";
import { DateRangePicker } from "react-date-range";
import { startOfWeek, endOfWeek } from "date-fns";
import dayjs from "dayjs";
import { MyContext } from "../../MyContextApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

function getThisWeekRange(weekStartsOn = 0) {
  const today = new Date();
  const start = startOfWeek(today, { weekStartsOn });
  const end = endOfWeek(today, { weekStartsOn });
  return [
    {
      startDate: start,
      endDate: end,
      key: "selection",
    },
  ];
}

function DateRangeModalPicker({
  open,
  onClose,
  onFromDateTimeChange,
  onToDateTimeChange,
  clearDates,
  title = "Select a Date Range",
  weekStartsOn = 0,
  maxDate = new Date(),
  months = 2,
}) {
  const { fromDateTime, setFromDateTime, toDateTime, setToDateTime } =
    useContext(MyContext);

  // Local state for react-date-range
  const [range, setRange] = useState(getThisWeekRange(weekStartsOn));

  // When modal opens or context changes, sync initial range
  useEffect(() => {
    if (fromDateTime && toDateTime) {
      setRange([
        {
          startDate: dayjs(fromDateTime).toDate(),
          endDate: dayjs(toDateTime).toDate(),
          key: "selection",
        },
      ]);
    } else {
      setRange(getThisWeekRange(weekStartsOn));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle clearDates like old component
  useEffect(() => {
    if (clearDates) {
      setFromDateTime(null);
      setToDateTime(null);
      onFromDateTimeChange && onFromDateTimeChange(null);
      onToDateTimeChange && onToDateTimeChange(null);
      setRange(getThisWeekRange(weekStartsOn));
    }
  }, [
    clearDates,
    onFromDateTimeChange,
    onToDateTimeChange,
    setFromDateTime,
    setToDateTime,
    weekStartsOn,
  ]);

  const handleDateChange = (item) => {
    setRange([item.selection]);
  };

  const handleApply = () => {
    const selected = range[0];
    if (!selected) return;

    // Keep dayjs API like your old DateTimePicker component
    const from = dayjs(selected.startDate).startOf("day");
    const to = dayjs(selected.endDate).endOf("day");

    setFromDateTime(from);
    setToDateTime(to);

    onFromDateTimeChange && onFromDateTimeChange(from);
    onToDateTimeChange && onToDateTimeChange(to);

    onClose && onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 400,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <div style={{ display: "flex" }}>
            <h2
              id="modal-title"
              style={{
                fontFamily: "'Host Grotesk', sans-serif",
                color: "#2c4e80",
                marginBottom: "16px",
              }}
            >
              {title}
            </h2>
          </div>
          <div
            className="date-close-button"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={onClose}
              style={{
                color: "black",
                padding: 0,
                width: "40px",
                height: "40px",
                minWidth: 0,
                paddingBottom: "10px",
              }}
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </Button>
          </div>
        </div>

        {/* Date Range Picker */}
        <DateRangePicker
          onChange={handleDateChange}
          showSelectionPreview={true}
          moveRangeOnFirstSelection={false}
          months={months}
          ranges={range}
          direction="horizontal"
          maxDate={maxDate}
        />

        {/* Apply button */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            sx={{
              mt: 2,
              backgroundColor: "white",
              fontFamily: "'Host Grotesk', sans-serif",
            }}
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

export default DateRangeModalPicker;
